import React, { useEffect, useState } from "react";
import Link from "next/link";
import { NextSeo, ArticleJsonLd } from "next-seo";
import {
  Box,
  Flex,
  Text,
  Link as ChakraLink,
  Divider,
} from "@chakra-ui/react";
import { formatDate } from "@/helpers/formatDate";
import { CategoryData } from "@/Types/types";

import CategoryListItem from "@/components/CategoryListItem";
import PostCardNoImg from "@/components/PostCardNoImg";
import Comments from "@/components/Comments";
import HeaderMenu from "@/components/HeaderMenu";
import Footer from "@/components/Footer";
import ShareButtons from "@/components/ShareButtons";
import { MarkdownRender } from "@/components/MarkdownRender";
import PostBanner from "@/components/PostBanner";
import Breadcrumbs from "@/components/Breadcrumbs";
import TableOfContent from "@/components/TableOfContent";
import AuthorCard from "@/components/AuthorCard";
import AuthorCardHorizontal from "@/components/AuthorCardHorizontal";

export default function PostPage(props: any) {
  const [date, setDate] = useState<String>();
  const postCategories: Record<"attributes", CategoryData>[] =
    props.post.data[0].attributes.category.data;
  const topics: Record<"attributes", CategoryData>[] = props.topics.data;

  const scrollToHeaders = () => {
    const ArticleWrapper = document.querySelector("article");
    const SecodLevelTitles = ArticleWrapper?.querySelectorAll("h2");

    SecodLevelTitles?.forEach((item) => {
      item.setAttribute("id", item.innerText.replace(/\s/g, ""));
    });
  };

  useEffect(() => {
    scrollToHeaders();
    setDate(formatDate(props.post.data[0].attributes.updatedAt));
  }, [props]);

  return (
    <Box bgColor="#FFFCF6">

      <HeaderMenu cookies={props.cookies.data} menu={props.header[0].items} submenu={props.navbar.data?.attributes.submenu_header} />
      <NextSeo
        title={props.post.data[0].attributes.metaTitle}
        description={props.post.data[0].attributes.metaDescription}
        canonical={
          props.post.data[0].attributes.canonicalURL &&
          props.post.data[0].attributes.canonicalURL
        }
        openGraph={{
          title: props.post.data[0].attributes.metaTitle,
          description: props.post.data[0].attributes.metaDescription,
          url: `${process.env.NEXT_PUBLIC_SITE_URL}/${props.post.data[0].attributes.url}`,
          type: "article",
          article: {
            publishedTime: props.post.data[0].attributes.updatedAt,
            authors: [
              props.post.data[0].attributes.author.data &&
              `${process.env.NEXT_PUBLIC_SITE_URL}/perfil/${props.post.data[0].attributes.author.data.attributes.url}`,
            ],
          },
          images: [
            {
              url: props.post.data[0].attributes.mainImage.data.attributes.url,
              alt: props.post.data[0].attributes.metaTitle,
            },
          ],
        }}
      />
      <ArticleJsonLd
        url={`${process.env.NEXT_PUBLIC_SITE_URL}/${props.post.data[0].attributes.url}`}
        title={props.post.data[0].attributes.metaTitle}
        images={[props.post.data[0].attributes.mainImage.data.attributes.url]}
        datePublished={props.post.data[0].attributes.updatedAt}
        authorName={[
          {
            name:
              props.post.data[0].attributes.author.data &&
              props.post.data[0].attributes.author.data.attributes.name,
            url:
              props.post.data[0].attributes.author.data &&
              `${process.env.NEXT_PUBLIC_SITE_URL}/blog/perfil/${props.post.data[0].attributes.author.data.attributes.url}`,
          },
        ]}
        description={props.post.data[0].attributes.metaDescription}
        isAccessibleForFree={true}
      />
      <Box maxWidth="1216px" width={{ base: "90%", lg: "100%" }} margin={{ base: "80px auto 0 auto", lg: "160px auto 0 auto" }} pb="32px" >
        <Breadcrumbs data={props.breadcrumbs} />
      </Box>
      <PostBanner data={props.post.data[0].attributes} />
      <Box
        maxWidth="1216px"
        margin="0 auto"
        width="90%"
        mb="32px"
        as="header"
        pt={{ base: "16px", lg: "80px" }}
        pb={{ base: "16px", lg: "80px" }}
      >
        <Flex
          columnGap="64px"
          alignItems="flex-start"
          flexDir={{ base: "column", lg: "row" }}
        >
          <Box
            bgColor="#fff"
            as="article"
            maxWidth="100%"
            overflow="hidden"
            boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);"
            borderRadius="8px"
          >
            <Box className="article" padding="32px">
              {props.post.data[0].attributes.content && (
                <MarkdownRender>
                  {props.post.data[0].attributes.content}
                </MarkdownRender>
              )}
            </Box>
            <Divider
              mt="32px"
              width="calc(100% - 64px)"
              display="block"
              bgColor="#CBD5E0"
              margin="0 auto"
            />
            <Flex
              mt="40px"
              mb="40px"
              padding="0 32px 0 32px"
              justifyContent="space-between"
              columnGap="32px"
              rowGap="20px"
              flexDirection={{ base: "column", md: "row" }}
            >
              <Flex
                columnGap="4px"
                alignItems="center"
                flexWrap="wrap"
                rowGap="20px"
              >
                {props.post.data[0].attributes.tags.data.length > 0 && (
                  <Text
                    marginRight="8px"
                    fontSize="14px"
                    fontWeight="600"
                    color="gray.500"
                  >
                    Tags
                  </Text>
                )}
                {props.post.data[0].attributes.tags.data &&
                  props.post.data[0].attributes.tags.data.map(
                    (item: any, i: number) => (
                      <ChakraLink
                        key={i}
                        as={Link}
                        padding="8px 12px"
                        borderRadius="8px"
                        bgColor="gray.100"
                        _hover={{ bgColor: "purple.50" }}
                        color="gray.500"
                        fontWeight="500"
                        fontSize="12px"
                        href={`/blog/tag/${item.attributes.url}`}
                      >
                        #{item.attributes.tag}
                      </ChakraLink>
                    )
                  )}
              </Flex>
              <ShareButtons url={`blog/${props.post.data[0].attributes.url}`} />
            </Flex>
            <Comments
              isPost
              id={props.post.data[0].id}
              item={props.comments && props.comments}
            />
            <Box padding="5px">
              <Flex
                bgColor="gray.50"
                columnGap="32px"
                rowGap="32px"
                borderBottomLeftRadius="8px"
                borderBottomRightRadius="8px"
                padding="27px 27px 43px 27px"
                justifyContent="space-between"
              >
                <Box flex="1 2 100%">
                  <Text
                    color="gray.500"
                    fontSize={{ base: "13", md: "16" }}
                    lineHeight="24px"
                    fontWeight="400px"
                  >
                    Artigo anterior
                  </Text>
                  <ChakraLink
                    as={Link}
                    _hover={{ textDecor: "none" }}
                    color="gray.600"
                    fontWeight="600"
                    fontSize={{ base: "13", md: "16" }}
                    marginTop="12px"
                    display="block"
                    href={
                      `/blog/${props.prevArticle.data[0]?.attributes.url}` ||
                      `/blog/${props.safeArticleOne.data[0].attributes.url}`
                    }
                  >
                    {props.prevArticle.data[0]?.attributes.title ||
                      props.safeArticleOne.data[0].attributes.title}
                  </ChakraLink>
                </Box>
                <Box textAlign="right" flex="1 2 100%">
                  <Text
                    color="gray.500"
                    fontSize={{ base: "13", md: "16" }}
                    lineHeight="24px"
                    fontWeight="400px"
                  >
                    Próximo Artigo
                  </Text>
                  <ChakraLink
                    as={Link}
                    _hover={{ textDecor: "none" }}
                    color="gray.600"
                    fontWeight="600"
                    fontSize={{ base: "13", md: "16" }}
                    marginTop="12px"
                    display="block"
                    href={
                      `/blog/${props.nextArticle.data[0]?.attributes.url}` ||
                      `/blog/${props.safeArticleTwo.data[0].attributes.url}`
                    }
                  >
                    {props.nextArticle.data[0]?.attributes.title ||
                      props.safeArticleTwo.data[0].attributes.title}
                  </ChakraLink>
                </Box>
              </Flex>
            </Box>
          </Box>
          <Box
            minWidth={{ base: "100%", md: "360px" }}
            as="aside"
            display="flex"
            flexDir="column"
            rowGap="64px"
            mt={{ base: "40px", lg: "0" }}
          >
            {props.post.data[0].attributes.author.data && (
              <AuthorCard authorArticlesTotal={props.authorArticlesTotal} post={props.post} hasDescription />
            )}
            {Array.from(
              props.post.data[0].attributes.content.matchAll(/^## (.*)$/gm)
            ).length > 0 && (
                <TableOfContent post={props.post.data[0].attributes.content} />
              )}
            <Box>
              <Text
                className="small-title"
                fontWeight="700"
                fontSize="18px"
                mb="12px"
              >
                Principais Categorias
              </Text>
              {topics.map((item, index) => (
                <CategoryListItem key={index} item={item.attributes} />
              ))}
            </Box>
            {props.post.data[0].attributes.relatedArticles.data.length > 0 && (
              <Box>
                <Text fontWeight="700" fontSize="18px" mb="12px">
                  Quem viu também gostou
                </Text>
                <Flex width="100%" flexDirection="column" rowGap="12px">
                  {props.post.data[0].attributes.relatedArticles.data.map(
                    (item: any, i: number) => (
                      <PostCardNoImg key={i} item={item} />
                    )
                  )}
                </Flex>
              </Box>
            )}
          </Box>
        </Flex>
      </Box>
      <Box display={{ base: "none", md: "block" }} mb="80px">
        <AuthorCardHorizontal author={props.post.data[0].attributes.author.data} />
      </Box>
      <Footer menu={props.footer[0].items} />
    </Box>
  );
}
